import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Check from '@material-ui/icons/Check';
import {
  Grid,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import FormatPrice from 'components/shared/FormatPrice';
import { Radio } from 'components/FormInputs';

const HYRA_STUGA_URL = 'https://www.stugknuten.com/sv/hyra-stuga';
const ARTICLE_BOOKINGHELP = '65359';

const LABELS = {
  1: (
    <Typography style={{ fontWeight: 500 }}>
      <FormattedMessage
        id="paymentOptions.id1title"
        defaultMessage="Advertisement only"
      />
    </Typography>
  ),
  10: (
    <Typography style={{ fontWeight: 500 }}>
      <FormattedMessage
        id="paymentOptions.id10title"
        defaultMessage="S-certified advertisement"
      />
    </Typography>
  ),
};

const DESCRIPTIONS = {
  1: (
    <>
      <Typography style={{ fontWeight: 'lighter', marginBottom: 10 }}>
        <FormattedMessage
          id="paymentOptions.id1desc"
          defaultMessage="No support is included regarding bookings, payment, contracts and insurances. You yourself are responsible for the entire rental process."
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id1benefits1"
          defaultMessage="The advertising period is 12 months (comparison price 125 SEK/month)"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id1benefits2"
          defaultMessage="Only the ad space is included with this option"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id1benefits3"
          defaultMessage="Limited possibilities to appear high up in search results"
        />
      </Typography>
    </>
  ),
  10: (
    <>
      <Typography style={{ fontWeight: 'lighter', marginBottom: 10 }}>
        <FormattedMessage
          id="paymentOptions.id10desc"
          defaultMessage="We recommend that you carry out booking, payment and agreement via Stugknuten. This package is very much appreciated by the guests."
        />
      </Typography>
      <Typography style={{ fontWeight: 500, marginTop: 20, marginBottom: 5 }}>
        <FormattedMessage
          id="paymentOptions.id10benefits"
          defaultMessage="Benefits in brief:"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id10benefits1"
          defaultMessage="The more you rent out, the better ranking your ad get in search results"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id10benefits2"
          defaultMessage="Extra insurance is included when payment is made through Stugknuten"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id10benefits3"
          defaultMessage="No advertising fee, instead we deduct 12% when doing payout to you bank account"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id10benefits4"
          defaultMessage="Verified reviews from guests make your ad more attractive"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id10benefits5"
          defaultMessage="Your ad gets a ranking boost for the first year when you switch to this model"
        />
      </Typography>
      <Typography style={{ marginBottom: 5 }}>
        <Check style={{ fontSize: 14 }} />&nbsp;
        <FormattedMessage
          id="paymentOptions.id10benefits6"
          defaultMessage="We will help you with any questions that may arise"
        />
      </Typography>
      <Typography style={{ fontWeight: 'lighter', marginTop: 20, marginBottom: 10 }}>
        <FormattedMessage
          id="paymentOptions.id10extra"
          defaultMessage="You can read more about the booking system {linkHere}"
          values={{
            linkHere: (
              <a
                role="button"
                tabIndex={0}
                onClick={() => window.supportHeroWidget.show({ article: ARTICLE_BOOKINGHELP })}
                style={{ textDecoration: 'underline' }}
              >
                <FormattedMessage
                  id="here"
                  defaultMessage="here"
                />
              </a>
            ),
          }}
        />
      </Typography>
      <Typography style={{ fontWeight: 'lighter', marginBottom: 10 }}>
        <FormattedMessage
          id="paymentOptions.id10extra2"
          defaultMessage="Ads on this model that have received good reviews are the ones that appear high up in search results, you can see how they are presented {linkHere}"
          values={{
            linkHere: (
              <a
                role="button"
                tabIndex={0}
                style={{ textDecoration: 'underline' }}
                target="_blank"
                rel="noreferrer"
                href={HYRA_STUGA_URL}
              >
                <FormattedMessage
                  id="here"
                  defaultMessage="here"
                />
              </a>
            ),
          }}
        />
      </Typography>
    </>
  ),
};

const styles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(3)}px ${theme.spacing(2)}px ${theme.spacing(2)}px`,
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  current: {
    backgroundColor: theme.palette.primary[400],
    display: 'inline-block',
    marginTop: -theme.spacing(1) * 1.6,
    borderRadius: theme.spacing(1),
    color: theme.palette.common.white,
    padding: `${theme.spacing(0.25)}px ${theme.spacing(0.5)}px`,
    position: 'absolute',
    right: theme.spacing(3.6),
  },
  selected: {
    boxShadow: `0 0 8px ${theme.palette.primary[500]}`,
  },
  price: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  priceText: {
    fontWeight: 500,
    marginRight: 25,
  },
  smarkLogo: {
    position: 'absolute',
    top: '-15px',
    maxWidth: '40px',
  },
}));

const PlansFormSection = ({
  plans,
  formValues,
  onChange,
  disabled,
  isCurrentFixed,
}) => {
  const classes = styles();

  return (
    <Grid container spacing={2}>
      {
        plans.map((plan, i) => {
          return (
            <Grid item xs={12} md={6} key={i}>
              <Paper
                className={classNames(
                  classes.root,
                  formValues.payment == plan.id && classes.selected, // eslint-disable-line
                )}
              >
                {
                  i === 0 && (
                    <img
                      src="data:image/png;base64,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"
                      className={classes.smarkLogo}
                      alt="S-märkning"
                    />
                  )
                }
                <Grid container>
                  <Grid item xs={9}>
                    <Radio
                      name="payment"
                      value={plan.id}
                      checked={formValues.payment == plan.id} // eslint-disable-line
                      onChange={() => {
                        onChange('payment')(plan.id);
                      }}
                      errors={{}}
                      label={LABELS[plan.id]}
                      disabled={disabled}
                    />
                    {isCurrentFixed && plan.id === 1 && (
                      <div className={classes.current}>
                        <FormattedMessage
                          id="Current"
                          defaultMessage="&nbsp;&nbsp;&nbsp;Current&nbsp;&nbsp;&nbsp;"
                        />
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={3} className={classes.price}>
                    <Typography align="right" className={classes.priceText}>
                      {plan.discount ? (
                        <>
                          <FormatPrice price={plan.price + plan.discount.price} />
                        </>
                      ) :
                        <FormatPrice price={plan.price} />}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {DESCRIPTIONS[plan.id]}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })
      }
    </Grid>
  );
};

PlansFormSection.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.shape({})),
  formValues: PropTypes.shape({
    payment: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isCurrentFixed: PropTypes.bool,
};

export default PlansFormSection;
